import { useState } from 'react';
import PropTypes from 'prop-types';
import FullScreenDialog from 'Components/Molecules/FullScreenDialog';
import useInvitations from 'Hooks/useInvitations';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import AvatarUser from 'Components/Atoms/AvatarUser/AvatarUser';
import Icon from 'Components/Atoms/Icon/Icon';
import { useUser } from 'Components/Organisms/UserProvider/useUser';
import { Trans, useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import { cn } from 'Utils/cn';

const captilize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const ACTIONS = {
    ACCEPT: 'accept',
    REJECT: 'reject',
};

export default function PendingInvitationDialog(props) {
    const { invitation, open, onClose } = props;
    const { user } = useUser();
    const { acceptInvitation, rejectInvitation } = useInvitations();

    const { t } = useTranslation('common');
    const [processingAction, setProcessingAction] = useState(null);
    const { showUnknownError } = useErrors();

    const handleAccept = async () => {
        setProcessingAction(ACTIONS.ACCEPT);
        try {
            await acceptInvitation(invitation.id);
        } catch (error) {
            showUnknownError(error);
        } finally {
            setProcessingAction(null);
            onClose();
        }
    };

    const handleReject = async () => {
        setProcessingAction(ACTIONS.REJECT);
        try {
            await rejectInvitation(invitation.id);
        } catch (error) {
            showUnknownError(error);
        } finally {
            setProcessingAction(null);
            onClose();
        }
    };

    return (
        <FullScreenDialog
            open={open}
            onClose={onClose}
            size="small"
            showCloseButton
            classes={{
                container: 'h-full flex align-center',
            }}
        >
            <div className="pb-6 m-auto">
                <div className="text-center mb-6">
                    <div className="flex items-center justify-center mb-6">
                        <AvatarUser className="shadow-lg" name={invitation.name} size={65} />
                        <div className="absolute z-10 bg-white p-0.5 rounded-full shadow-lg text-dark-blue">
                            <Icon name="plus" size={24} color="currentColor" />
                        </div>
                        <AvatarUser className="shadow-lg" name={user.name} size={65} />
                    </div>
                    <h4>
                        <Trans t={t} i18nKey="invitation-join">
                            {{ name: captilize(invitation.name) }}
                        </Trans>
                    </h4>
                    <div className="text-gray-500 pb-2">
                        <Trans t={t} i18nKey="invitation-join-description">
                            {{ name: captilize(invitation.name) }}
                        </Trans>
                    </div>
                </div>

                <div
                    className={cn(
                        'flex gap-2 justify-center mt-6 md:mt-0',
                        processingAction ? 'pointer-events-none' : '',
                    )}
                >
                    <Button variant="primary" isBusy={processingAction === ACTIONS.ACCEPT} onClick={handleAccept}>
                        {t('accept')}
                    </Button>
                    <Button isBusy={processingAction === ACTIONS.REJECT} onClick={handleReject}>
                        {t('reject')}
                    </Button>
                </div>
            </div>
        </FullScreenDialog>
    );
}

PendingInvitationDialog.propTypes = {
    invitation: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
